// API Service for Native Brilliance Assessment
const API_BASE_URL = 'https://nbassessment-l8rxu.ondigitalocean.app/api'; // Temporarily hardcoded
// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Helper function for API calls
const apiCall = async (endpoint, options = {}) => {
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'API request failed');
    }

    return await response.json();
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

// User API calls
export const userAPI = {
  signup: async (userData) => {
    const response = await apiCall('/users/signup', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
    
    // Also store in localStorage for quick access
    if (response.success) {
      localStorage.setItem('currentUser', JSON.stringify(response.user));
      localStorage.setItem('userId', response.user.user_id);
    }
    
    return response;
  },

  getUser: async (userId) => {
    return await apiCall(`/users/${userId}`);
  },

  updateUser: async (userId, updates) => {
    return await apiCall(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  }
};

// Assessment API calls
export const assessmentAPI = {
  start: async (userId) => {
    const response = await apiCall('/assessments/start', {
      method: 'POST',
      body: JSON.stringify({ userId })
    });
    
    // Store assessment ID in localStorage
    if (response.success) {
      localStorage.setItem('currentAssessmentId', response.assessment.result_id);
    }
    
    return response;
  },

  saveModuleAttempt: async (attemptData) => {
    return await apiCall('/assessments/module-attempt', {
      method: 'POST',
      body: JSON.stringify(attemptData)
    });
  },

  complete: async (resultId, totalTimeSeconds) => {
    return await apiCall('/assessments/complete', {
      method: 'POST',
      body: JSON.stringify({ resultId, totalTimeSeconds })
    });
  },

  getUserAssessments: async (userId) => {
    return await apiCall(`/assessments/user/${userId}`);
  },

  getResult: async (resultId) => {
    return await apiCall(`/assessments/result/${resultId}`);
  }
};

// Percentile API calls
export const percentileAPI = {
  calculate: async (metric, score) => {
    return await apiCall(`/percentiles/calculate?metric=${metric}&score=${score}`);
  },

  getDistribution: async (metric) => {
    return await apiCall(`/percentiles/distribution/${metric}`);
  },

  getStatistics: async () => {
    return await apiCall('/percentiles/statistics');
  }
};

// Helper function to save module results
export const saveModuleResults = async (moduleId, results) => {
  try {
    const userId = localStorage.getItem('userId');
    const resultId = localStorage.getItem('currentAssessmentId');
    
    if (!userId || !resultId) {
      console.error('Missing user or assessment ID');
      return null;
    }

    const attemptData = {
      resultId,
      userId,
      moduleId,
      questionsAnswered: results.questionsAnswered,
      correctAnswers: results.correctAnswers,
      pointsEarned: results.pointsEarned,
      maxPointsPossible: results.maxPointsPossible,
      difficultySequence: results.difficultySequence || null,
      instanceIds: results.instanceIds || null,
      responses: results.responses || {},
      duration: results.duration || null
    };

    return await assessmentAPI.saveModuleAttempt(attemptData);
  } catch (error) {
    console.error('Error saving module results:', error);
    // Fallback to localStorage if API fails
    const localResults = JSON.parse(localStorage.getItem('assessmentResults') || '{}');
    localResults[moduleId] = results;
    localStorage.setItem('assessmentResults', JSON.stringify(localResults));
    return null;
  }
};

// Helper function to calculate and save final results
export const completeAssessment = async () => {
  try {
    const resultId = localStorage.getItem('currentAssessmentId');
    const startTime = localStorage.getItem('assessmentStartTime');
    
    if (!resultId) {
      console.error('No assessment ID found');
      return null;
    }

    const totalTimeSeconds = startTime 
      ? Math.floor((Date.now() - parseInt(startTime)) / 1000)
      : null;

    const response = await assessmentAPI.complete(resultId, totalTimeSeconds);
    
    // Clear assessment data from localStorage
    localStorage.removeItem('currentAssessmentId');
    localStorage.removeItem('assessmentStartTime');
    localStorage.removeItem('assessmentResults');
    
    return response;
  } catch (error) {
    console.error('Error completing assessment:', error);
    return null;
  }
};

export default {
  userAPI,
  assessmentAPI,
  percentileAPI,
  saveModuleResults,
  completeAssessment
};